import { DataStoreQueryCondition, DataStoreQueryConditionType } from "./DataStoreQueryCondition";
import { IUserInfo } from "./IUserInfo";
import { IBaseDataObject } from "./BaseDataObject";
import { DataUtilities } from "../../Common/DataUtilities";
import { OrConditions } from "./OrConditions";
import { DataStoreFieldChange } from "./DataStoreFieldChange";

export interface IDataStore<T extends IBaseDataObject> {
    get(user: IUserInfo, id: string): Promise<T>;

    getByAlternateId(user: IUserInfo, alternateIdName: string, alternateId: string): Promise<T>;

    getByIds(user: IUserInfo, ids: string[]): Promise<T[]>;

    getAll(user: IUserInfo): Promise<T[]>;

    store(user: IUserInfo, item: T | T[]): Promise<T>;

    existsById(user: IUserInfo, id: string): Promise<boolean>;

    existsByAlternateId(user: IUserInfo, alternateIdName: string, alternateId: string): Promise<boolean>;

    query(user: IUserInfo, conditions: DataStoreQueryCondition | DataStoreQueryCondition[]): Promise<T[]>;

    queryWithOrConditions(user: IUserInfo, conditions: OrConditions[]): Promise<T[]>;

    queryForSingle(user: IUserInfo, conditions: DataStoreQueryCondition | DataStoreQueryCondition[]): Promise<T>;

    queryForCount(user: IUserInfo, conditions: DataStoreQueryCondition | DataStoreQueryCondition[]): Promise<number>;

    delete(user: IUserInfo, id: string): Promise<boolean>;

    deleteWhere(user: IUserInfo, conditions: DataStoreQueryCondition | DataStoreQueryCondition[]): Promise<number>;

    updateWhere(
        user: IUserInfo,
        conditions: DataStoreQueryCondition | DataStoreQueryCondition[],
        fieldChange: DataStoreFieldChange | DataStoreFieldChange[],
    ): Promise<number>;

    update(user: IUserInfo, id: string, fieldChange: DataStoreFieldChange | DataStoreFieldChange[]): Promise<T>;

    updateByAlternateId(
        user: IUserInfo,
        alternateIdName: string,
        alternateId: string,
        fieldChange: DataStoreFieldChange | DataStoreFieldChange[],
    ): Promise<T>;
}

export abstract class BaseDataStore<T extends IBaseDataObject> implements IDataStore<T> {
    abstract delete(user: IUserInfo, id: string): Promise<boolean>;

    abstract get(user: IUserInfo, id: string): Promise<T>;

    getByAlternateId(user: IUserInfo, alternateIdName: string, alternateId: string): Promise<T> {
        return this.queryForSingle(
            user,
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, alternateIdName, alternateId),
        );
    }

    queryForSingle(user: IUserInfo, conditions: DataStoreQueryCondition | DataStoreQueryCondition[]): Promise<T> {
        return new Promise<T>((result, reject) => {
            this.query(user, conditions)
                .then((res) => {
                    if (res && res.length > 0) {
                        if (res.length > 1) reject("More than one item was returned for single query");

                        result(res[0]);
                    } else {
                        if (!Array.isArray(conditions)) conditions = [conditions];

                        let conditionsstring = conditions.map((c) => c.toString()).join(" && ");

                        reject("could not find object [" + conditionsstring + "]");
                    }
                })
                .catch((reason) => {
                    reject("could not find object - reason " + reason);
                });
        });
    }

    abstract getAll(user: IUserInfo): Promise<T[]>;

    abstract getByIds(user: IUserInfo, ids: string[]): Promise<T[]>;

    abstract query(user: IUserInfo, conditions: DataStoreQueryCondition | DataStoreQueryCondition[]): Promise<T[]>;

    abstract queryForCount(
        user: IUserInfo,
        conditions: DataStoreQueryCondition | DataStoreQueryCondition[],
    ): Promise<number>;

    abstract store(user: IUserInfo, item: T[] | T): Promise<T>;

    existsByAlternateId(user: IUserInfo, alternateIdName: string, alternateId: string): Promise<boolean> {
        return new Promise<boolean>((accept, reject) => {
            this.query(
                user,
                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, alternateIdName, alternateId),
            )
                .then((res) => {
                    if (res && res.length > 0) {
                        if (res.length > 1) reject("More than one item was returned for single query");

                        accept(true);
                    } else {
                        accept(false);
                    }
                })
                .catch((reason) => {
                    reject(reason);
                });
        });
    }

    existsById(user: IUserInfo, id: string): Promise<boolean> {
        return this.existsByAlternateId(user, "id", id);
    }

    async deleteWhere(
        user: IUserInfo,
        conditions: DataStoreQueryCondition | DataStoreQueryCondition[],
    ): Promise<number> {
        let items = await this.query(user, conditions);

        for (let i of items) {
            this.delete(user, i.id!);
        }

        return items.length;
    }

    async updateWhere(
        user: IUserInfo,
        conditions: DataStoreQueryCondition | DataStoreQueryCondition[],
        fieldChange: DataStoreFieldChange | DataStoreFieldChange[],
    ): Promise<number> {
        let items = await this.query(user, conditions);

        for (let i of items) {
            this.setUpdateFields(i, fieldChange);
        }

        return items.length;
    }

    async update(user: IUserInfo, id: string, fieldChange: DataStoreFieldChange | DataStoreFieldChange[]): Promise<T> {
        let item = await this.get(user, id);

        this.setUpdateFields(item, fieldChange);

        return item;
    }

    async updateByAlternateId(
        user: IUserInfo,
        alternateIdName: string,
        alternateId: string,
        fieldChange: DataStoreFieldChange | DataStoreFieldChange[],
    ): Promise<T> {
        let item = await this.getByAlternateId(user, alternateIdName, alternateId);

        this.setUpdateFields(item, fieldChange);

        return item;
    }

    async queryWithOrConditions(user: IUserInfo, conditions: OrConditions[]): Promise<T[]> {
        let results: T[] = [];

        let promises: Promise<void>[] = [];

        for (let c of conditions) {
            let queryConditions = c.orConditions;
            promises.push(
                new Promise<void>((accept, reject) => {
                    this.query(user, queryConditions)
                        .then((items) => {
                            results.push(...items);
                            accept();
                        })
                        .catch((err) => reject(err));
                }),
            );
        }

        await Promise.all(promises);

        let uniqueIds: string[] = [];
        results = results.filter((element) => {
            const isDuplicate = uniqueIds.includes(element.id!);

            if (!isDuplicate) {
                uniqueIds.push(element.id!);

                return true;
            }

            return false;
        });

        // reduce duplicates

        return results;
    }

    private setUpdateFields(i: T, fieldChange: DataStoreFieldChange | DataStoreFieldChange[]) {
        if (!Array.isArray(fieldChange)) {
            fieldChange = [fieldChange];
        }

        for (let fc of fieldChange) {
            DataUtilities.set(i, fc.path, fc.value);
        }
    }
}
